import {
  batch,
  createSignal,
  type JSX,
  onCleanup,
  onMount,
  Show,
  untrack,
} from "solid-js";
import { produce } from "solid-js/store";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  SectorSelector,
  type SectorSelectorControls,
  SummaryRow,
} from "@/domains/freelance/taxonomy/components/sector-selector";
import {
  createSectorDraft,
  type SectorDraft,
} from "@/domains/freelance/taxonomy/selector/sector-draft";
import {
  categoryOptions,
  parentOf,
  subcategoriesOf,
  taxonomyStatus,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { useOnboardingStore } from "../../context/OnboardingContext";
import {
  hubDraftSaved,
  sectorValueFromSlots,
  withStoredOrigins,
} from "../../utils/hub-sector-draft";

/** Same texts as the profile's sector modal (AC11, AC25). */
const I18N = "profile.general_card.edit.sector" as const;

const lookup = { subcategoriesOf, parentOf };

const noop = () => undefined;

/** The tree is there: same test as the selector's (its skeleton shows until then). */
const treeReady = () =>
  taxonomyStatus() === "ready" ||
  categoryOptions().some((option) => subcategoriesOf(option.value).length > 0);

/** What the open modal hands to the `Dialog` around it. */
type HubSession = {
  requestClose: () => void;
  focusInitial: () => void;
};

/**
 * The modal of the hub's «Settore» card (AC25): the profile modal's selector
 * and texts, but «Salva» writes only the onboarding store and closes. Nothing
 * reaches the backend until the final `POST /profile/onboarding` (AC19).
 *
 * It owns its `Dialog`, so that X, Escape and a click outside go through the
 * discard question (AC14); the trigger (the `HubCard`) comes in as `children`,
 * and Kobalte gives focus back to it on close.
 */
export const SectorHubModal = (props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: JSX.Element;
}) => {
  let session: HubSession | undefined;
  const close = () => props.onOpenChange(false);

  /*
   * The focus on open (AC26): the selector's own target, not Kobalte's first
   * tabbable. **Untracked**: Kobalte runs this handler inside one of its
   * effects, and a signal read here would replay the open focus (and the
   * return one) at every change. Without the editor yet (the tree is loading)
   * Kobalte's default stays.
   */
  const focusOnOpen = (event: Event) => {
    const current = session;
    if (!current) return;
    event.preventDefault();
    untrack(current.focusInitial);
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (open) props.onOpenChange(true);
        else if (session) session.requestClose();
        else close();
      }}
    >
      {props.children}
      <DialogContent class="max-w-2xl bg-white" onOpenAutoFocus={focusOnOpen}>
        <SectorHubBody
          onSession={(next) => {
            session = next;
          }}
          onClose={close}
        />
      </DialogContent>
    </Dialog>
  );
};

/**
 * Mounted at every open (Kobalte mounts the content only while present): the
 * title, the live region and, once the tree is there, the editor.
 */
const SectorHubBody = (props: {
  onSession: (session: HubSession | undefined) => void;
  onClose: () => void;
}) => {
  const { t } = useI18n();
  /** The tree arrived after the open: the editor takes the focus itself. */
  const readyAtOpen = untrack(treeReady);

  /*
   * The modal's own live region (the selector writes into it): always mounted,
   * only the text changes. The same text twice would not change the DOM, so it
   * is emptied and written again a moment later.
   */
  const [announcement, setAnnouncement] = createSignal("");
  let repeat: ReturnType<typeof setTimeout> | undefined;
  const announce = (text: string) => {
    clearTimeout(repeat);
    if (untrack(announcement) !== text) {
      setAnnouncement(text);
      return;
    }
    setAnnouncement("");
    repeat = setTimeout(() => setAnnouncement(text), 100);
  };
  onCleanup(() => clearTimeout(repeat));

  return (
    <div class="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>{t(`${I18N}.title`)}</DialogTitle>
        <DialogDescription>{t(`${I18N}.description`)}</DialogDescription>
      </DialogHeader>

      <Show
        when={treeReady()}
        fallback={
          <LoadingSelector announce={announce} onClose={props.onClose} />
        }
      >
        <SectorHubEditor
          announce={announce}
          focusOnMount={!readyAtOpen}
          onSession={props.onSession}
          onClose={props.onClose}
        />
      </Show>

      <p aria-live="polite" class="sr-only">
        {announcement()}
      </p>
    </div>
  );
};

/**
 * The draft is born once, from the store as it is at open and untracked: it
 * needs the tree to put each subcategory under its sector. «Annulla» and the
 * discard question drop it, so the store stays as it was at open.
 */
const SectorHubEditor = (props: {
  announce: (text: string) => void;
  focusOnMount: boolean;
  onSession: (session: HubSession | undefined) => void;
  onClose: () => void;
}) => {
  const [store, setStore] = useOnboardingStore();

  const draft = untrack(() => {
    const value = store.sector.value;
    const created = createSectorDraft(
      {
        saved: hubDraftSaved(value),
        proposal: null,
        source: store.sector.source,
        container: "onboarding",
      },
      lookup,
    );
    created.restore(withStoredOrigins(created.snapshot(), value));
    return created;
  });

  let controls: SectorSelectorControls | undefined;
  let saveButton: HTMLButtonElement | undefined;

  const canSave = () => !!draft.slots[0];

  /** «Chiudere senza salvare?» open (AC14). */
  const [asking, setAsking] = createSignal(false);
  /** Where the focus was in the modal when the question opened. */
  let focusBeforeAsking: HTMLElement | null = null;

  /** X, Escape, a click outside and «Annulla»: clean, it closes at once. */
  const requestClose = () => {
    if (!draft.isDirty()) {
      props.onClose();
      return;
    }
    const active = document.activeElement;
    focusBeforeAsking =
      active instanceof HTMLElement &&
      saveButton?.closest('[role="dialog"]')?.contains(active)
        ? active
        : null;
    setAsking(true);
  };

  /**
   * «Continua a modificare», or Escape and a click outside on the question:
   * the draft is intact, and the focus goes back where it was (or to «Salva»).
   */
  const keepEditing = () => {
    setAsking(false);
    const back = focusBeforeAsking?.isConnected
      ? focusBeforeAsking
      : saveButton;
    focusBeforeAsking = null;
    back?.focus();
  };

  /** «Chiudi senza salvare»: the draft is dropped, the store never changed. */
  const leave = () => {
    batch(() => {
      setAsking(false);
      props.onClose();
    });
  };

  const save = () => {
    if (!canSave()) return;
    setStore(
      produce((state) => {
        state.sector.value = sectorValueFromSlots(draft.snapshot());
        state.sector.reviewed = true;
        state.sector.error = null;
      }),
    );
    // No `accept_suggested_job_category` here (§13 n.5): the event keeps its
    // `surface: "linkedin_hub"` in the type (AC35), but during the onboarding
    // `/me` has no `suggested_job_category` (the cron reads completed profiles
    // only), so the draft has no proposal to accept: the hub's suggestions come
    // from LinkedIn.
    props.onClose();
  };

  props.onSession({
    requestClose,
    focusInitial: () => controls?.focusInitial(),
  });
  onCleanup(() => props.onSession(undefined));
  onMount(() => {
    if (props.focusOnMount) controls?.focusInitial();
  });

  return (
    <>
      <SectorSelector
        draft={draft}
        announce={(text) => props.announce(text)}
        controls={(api) => {
          controls = api;
        }}
      />
      <HubSectorFooter
        draft={draft}
        canSave={canSave()}
        saveRef={(el) => {
          saveButton = el;
        }}
        onCancel={requestClose}
        onSave={save}
      />
      {/* Inside the content, as in `CompetenceModal`: Kobalte registers it as
          a nested layer, so a click inside the question is not a click
          outside the modal, and Escape closes only the question. */}
      <DiscardQuestion
        open={asking()}
        onKeepEditing={keepEditing}
        onLeave={leave}
      />
    </>
  );
};

/**
 * While the tree loads (skeleton) or after it failed («Riprova»): the selector
 * shows its own states on an empty draft, «Salva» stays off.
 */
const LoadingSelector = (props: {
  announce: (text: string) => void;
  onClose: () => void;
}) => {
  const draft = createSectorDraft(
    {
      saved: {
        job_category: null,
        job_category_secondary: null,
        job_subcategories: [],
      },
      proposal: null,
      source: null,
      container: "onboarding",
    },
    lookup,
  );
  return (
    <>
      <SectorSelector draft={draft} announce={(text) => props.announce(text)} />
      <HubSectorFooter
        draft={draft}
        canSave={false}
        onCancel={() => props.onClose()}
        onSave={noop}
      />
    </>
  );
};

/** «Salvando: …», «Annulla» and «Salva». */
const HubSectorFooter = (props: {
  draft: SectorDraft;
  canSave: boolean;
  saveRef?: (el: HTMLButtonElement) => void;
  onCancel: () => void;
  onSave: () => void;
}) => {
  const { t } = useI18n();
  return (
    <div class="flex flex-col-reverse gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
      <SummaryRow
        draft={props.draft}
        verb={t("taxonomy.sector.summary.saveVerb")}
      />
      <div class="flex justify-end gap-2 sm:ml-auto">
        <Button variant="outline" onClick={() => props.onCancel()}>
          {t(`${I18N}.cancel`)}
        </Button>
        {/* `aria-disabled`, not `disabled`: «Salva» stays in the Tab order
            with its state read out. The house `Button` styles only
            `disabled:`, hence the classes. */}
        <Button
          ref={(el) => props.saveRef?.(el)}
          class="aria-disabled:pointer-events-none aria-disabled:opacity-50 aria-disabled:active:scale-100"
          aria-disabled={props.canSave ? undefined : "true"}
          onClick={() => props.onSave()}
        >
          {t(`${I18N}.save`)}
        </Button>
      </div>
    </div>
  );
};

/**
 * «Chiudere senza salvare?» (AC14): the house `Dialog`, no motion of its own.
 * Escape, a click outside and its X mean «Continua a modificare».
 */
const DiscardQuestion = (props: {
  open: boolean;
  onKeepEditing: () => void;
  onLeave: () => void;
}) => {
  const { t } = useI18n();
  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => !open && props.onKeepEditing()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`${I18N}.discard.title`)}</DialogTitle>
          <DialogDescription>
            {t(`${I18N}.discard.description`)}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => props.onKeepEditing()}>
            {t("profile.general_card.edit.competence.discard.cancel")}
          </Button>
          <Button variant="destructive" onClick={() => props.onLeave()}>
            {t(`${I18N}.discard.confirm`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
